/* eslint-disable no-console */
/* eslint-disable max-len */
import React, { useCallback, forwardRef, useImperativeHandle } from 'react';
import { toast } from 'react-toastify';
import { validatePodcastUrl, processPodcastUrl, estimatePodcastFileSize } from '../EmbedHelpers';
import type { EmbedPodcastsProps } from '../types';
import { handleAudioUpload } from '../../../../../Creation/Podcast/helpers/AudioUploadHelper';

export interface EmbedPodcastsRef {
	process: () => Promise<void>;
}

const EmbedPodcasts = forwardRef<EmbedPodcastsRef, EmbedPodcastsProps>(({
	url,
	isProcessing,
	onUploadComplete,
	onError,
	companyId
}, ref) =>
{
	/**
	 * Validates podcast URL
	 */
	const validatePodcastUrlFormat = useCallback((inputUrl: string): boolean =>
	{
		// For podcasts, validate the file extension more strictly
		if (!validatePodcastUrl(inputUrl))
		{
			onError('URL must point to a supported audio format (.mp3)');

			return false;
		}

		return true;
	}, [ onError ]);

	/**
	 * Processes and uploads podcast
	 */
	const processAndUploadPodcast = useCallback(async (inputUrl: string): Promise<void> =>
	{
		try
		{
			// 1. Validate the podcast URL
			if (!validatePodcastUrlFormat(inputUrl))
			{
				return;
			}

			// 2. Process Podcast URL to get basic metadata
			let processedData;

			try
			{
				processedData = processPodcastUrl(inputUrl);

				if (!processedData)
				{
					throw new Error('Failed to process Podcast URL');
				}
			}
			catch (podcastError)
			{
				const errorMessage = podcastError instanceof Error ? podcastError.message : 'Failed to process podcast';

				onError(errorMessage);

				return;
			}

			// 3. Try to estimate file size
			try
			{
				const estimatedSize = await estimatePodcastFileSize(inputUrl);

				if (processedData.metadata)
				{
					processedData.metadata.fileSize = estimatedSize;
				}
			}
			catch (sizeError)
			{
				console.warn('Could not estimate file size:', sizeError);
				// Continue without file size
			}

			// 4. Use handleAudioUpload directly - same as file uploads
			if (companyId)
			{
				try
				{
					const audioPayload = await handleAudioUpload(
						inputUrl, // URL string
						companyId, // Company ID
						{
							title : processedData.title
						},
						true // isUrl flag
					);

					processedData = {
						...processedData,
						...audioPayload,
						source             : 'podcast_url',
						skipParentCallback : false
					};
				}
				catch (uploadError)
				{
					console.error('Error in handleAudioUpload:', uploadError);
					throw uploadError;
				}
			}

			// 5. Notify parent for final processing.
			onUploadComplete(processedData);
		}
		catch (uploadError)
		{
			console.error('Error processing podcast URL:', uploadError);
			const errorMessage = uploadError instanceof Error
				? uploadError.message
				: 'Failed to process podcast URL. Please try again.';

			onError(errorMessage);
			toast.error('Failed to process podcast URL. Please try again.');
		}
	}, [ validatePodcastUrlFormat, companyId, onUploadComplete, onError ]);

	/**
	 * Main processing function called by parent
	 */
	const processPodcast = useCallback(async (): Promise<void> =>
	{
		if (isProcessing || !url.trim()) return;

		await processAndUploadPodcast(url);
	}, [ url, isProcessing, processAndUploadPodcast ]);

	// Expose the process function for parent to call
	useImperativeHandle(ref, () => ({
		process : processPodcast
	}));

	return null;
});

EmbedPodcasts.displayName = 'EmbedPodcasts';

export default EmbedPodcasts;